import React, { useMemo } from 'react'
import {
    Text,
    Box,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Link as ChakraLink,

    /* Hooks */
    useMultiStyleConfig
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import useDetectDevice from '../../../../hooks/use-detect-device'

/* Utils */
import { generateStyles } from '../../utils/utils'
import { noop } from '@salesforce/retail-react-app/app/utils/utils'
import { renderComponentsSwitch } from '../../utils/contentRenderer'

/* Contexts */
import { useGetLinkData } from '../../../../contexts/category-url-context'
import {openClaraChat} from '../../../../hooks/use-clara-chat'

const CtaItemBlock = ({
    CtaLinkText,
    OverwriteCtaLinkText,
    Styling,
    PreTitle,
    Title,
    Description,
    CtaItemBlockPreTitleStyle,
    CtaItemBlockTitleStyle,
    CtaItemBlockDescriptionStyle,
    CTABackgroundColor,
    Alignment,
    Icon,
    Image,
    ContainerCustomStyling = {},
    stylingTextOverwrite = {},
    stylingButtonOverwrite = {},
    stylingOutlinedOverwrite = {},
    customMediaContainerStyling = {},
    popoverContent = null,
    isTrigger = null,
    ctaBackgroundColorOverwrite = {},
    hideTitle = false,
    position = 'static',
    onClickCtaItemBlock = noop,
    variant = Styling || 'Button',
    noPadding = false,
    ...props
}) => {
    const getLinkData = useGetLinkData()
    const { isIOS, isMacOS, isMacOSChrome, isSafari, isAndroid } = useDetectDevice()
    const styles = useMultiStyleConfig('CtaItemBlock', {
        ...props,
        variant,
        Styling: Styling,
        CTABackgroundColor: CTABackgroundColor || ctaBackgroundColorOverwrite,
        Alignment: Alignment,
        position: position,
        noPadding: noPadding,
        isSafari: isSafari,
        isIOS: isIOS,
        isMacOS: isMacOS,
        isAndroid: isAndroid,
        isMacOSChrome: isMacOSChrome
    })

    const linkData = useMemo(() => {
        return getLinkData(props?.Link?.Expanded)
    }, [props?.Link?.Expanded])

    const renderButton = (variant, componentStyle, styleOverwrite, defaultText) => {
        // Use <a> tag for external URLs (http/https), React Router Link for internal paths
        const isOpenChat = linkData?.isOpenChat || false
        const isExternalUrl = linkData?.isExternalUrl || false
        const hasLink = !!linkData?.generatedURL

        const linkComponent = isTrigger ? 'Button' : isOpenChat ? 'button' : hasLink ? (isExternalUrl ? ChakraLink : Link) : 'span'
        const linkProps = isTrigger || isOpenChat || !hasLink
            ? {}
            : isExternalUrl
                ? { href: linkData?.generatedURL }
                : { to: linkData?.generatedURL }

        return (
            <Button
                as={linkComponent}
                variant={variant}
                {...linkProps}
                {...(hasLink && !isOpenChat && !linkData.stayOnSamePage && {
                    target: linkData?.target,
                    rel: linkData?.rel
                })}
                onClick={isOpenChat ? openClaraChat : isTrigger ? onClickCtaItemBlock : noop}
                alt={linkData?.AltText}
                {...generateStyles(props, {
                    ...componentStyle,
                    ...styleOverwrite
                })}
                sx={{...componentStyle, ...styleOverwrite}}>
                {Icon &&
                    renderComponentsSwitch(Icon?.Expanded, {
                        customMediaContainerStyling: { marginRight: {base: 4}, ...customMediaContainerStyling }
                    })
                }
                <Text {...(variant === 'Outlined' || variant === 'Button' ? styles.ctaText : {})}
                    {...styles.ctaText}sx={{...(variant === 'Outlined' || variant === 'Button' ? styles.ctaText : {})}}
                    as={'span'}>
                    {OverwriteCtaLinkText || CtaLinkText || defaultText}
                </Text>
            </Button>
        )
    }

    return (
        <Box {...styles.container} {...ContainerCustomStyling} data-block-type="CtaItemBlock">
            {/* Image */}
            {Image &&
                <Box mb={{ base: 4, lg: 6 }}>
                    {renderComponentsSwitch(Image?.Expanded, {
                        customMediaContainerStyling: {
                            width: '100%',
                            maxWidth: { base: '100px', lg: '140px' },
                            margin: '0 auto',
                            display: 'flex',
                            justifyContent: 'center'
                        }
                    })}
                </Box>
            }

            {/* Pre title */}
            {PreTitle !== '' &&
                <Text {...generateStyles(CtaItemBlockPreTitleStyle, { ...styles.CtaItemBlockPreTitleStyle })}>
                    {PreTitle}
                </Text>
            }

            {/* Title */}
            {Title !== '' && !hideTitle &&
                <Text {...generateStyles(CtaItemBlockTitleStyle, { ...styles.CtaItemBlockTitleStyle })}>
                    {Title}
                </Text>
            }

            {/* Description */}
            {Description !== '' &&
                <Text
                    {...generateStyles(CtaItemBlockDescriptionStyle, {
                        ...styles.CtaItemBlockDescriptionStyle,
                        ...((CtaLinkText || OverwriteCtaLinkText) && {marginBottom: 0})
                    })}
                >
                    {Description}
                </Text>
            }

            {/* Buttons - only show if there's link text */}
            {(CtaLinkText || OverwriteCtaLinkText) && (
                <Box>
                    {popoverContent ? (
                        <Popover placement='top-start'>
                            {Styling === 'Text' && <PopoverTrigger data-testid="cta-text-popover-trigger">{renderButton('link', styles.link, stylingTextOverwrite, 'Anchor')}</PopoverTrigger>}
                            {Styling === 'Outlined' && <PopoverTrigger data-testid="cta-outlined-popover-trigger">{renderButton('outline', styles.cta, stylingOutlinedOverwrite, 'Outlined CTA')}</PopoverTrigger>}
                            {Styling === 'Button' && <PopoverTrigger data-testid="cta-button-popover-trigger">{renderButton('outline', styles.cta, stylingButtonOverwrite, 'Button CTA')}</PopoverTrigger>}
                            <PopoverContent {...styles.popoverContent}>
                                <PopoverBody {...styles.popoverBody}>
                                    {popoverContent}
                                </PopoverBody>
                            </PopoverContent>
                        </Popover>
                    ) : (
                        <>
                            {Styling === 'Text' &&
                                renderButton(
                                    'link',
                                    PreTitle || Title || Description
                                        ? styles.linkWithText
                                        : styles.link,
                                    stylingTextOverwrite,
                                    'Anchor'
                                )}
                            {Styling === 'Outlined' && renderButton('outline', styles.cta, stylingOutlinedOverwrite, 'Outlined CTA')}
                            {Styling === 'Button' && renderButton('outline', styles.cta, stylingButtonOverwrite, 'Button CTA')}
                        </>
                    )}
                </Box>
            )}
        </Box>
    )
}

CtaItemBlock.propTypes = {
    CtaLinkText: PropTypes.string,
    OverwriteCtaLinkText: PropTypes.string,
    Styling: PropTypes.string,
    PreTitle: PropTypes.string,
    Title: PropTypes.string,
    Description: PropTypes.string,
    CtaItemBlockPreTitleStyle: PropTypes.object,
    CtaItemBlockTitleStyle: PropTypes.object,
    CtaItemBlockDescriptionStyle: PropTypes.object,
    CTABackgroundColor: PropTypes.string,
    Alignment: PropTypes.string,
    Icon: PropTypes.object,
    Image: PropTypes.object,
    ContainerCustomStyling: PropTypes.object,
    stylingTextOverwrite: PropTypes.object,
    stylingButtonOverwrite: PropTypes.object,
    stylingOutlinedOverwrite: PropTypes.object,
    popoverContent: PropTypes.node,
    isTrigger: PropTypes.bool,
    ctaBackgroundColorOverwrite: PropTypes.object,
    hideTitle: PropTypes.bool,
    position: PropTypes.string,
    onClickCtaItemBlock: PropTypes.func,
    variant: PropTypes.string
}

export default CtaItemBlock
